// ErrorBoundary.js
// It catches any render-time JS error in its child tree and shows a
// friendly fallback instead of a blank/white screen.

import React from 'react';

// ─── Default fallback UI ────────────────────────────────────────────────────

const DefaultFallback = ({ error, componentName, onReset }) => (
    <div style={styles.wrapper}>
        <div style={styles.card}>
            <div style={styles.iconRow}>
                <span style={styles.icon}>⚠</span>
            </div>
            <h2 style={styles.title}>Something went wrong</h2>
            {componentName && (
                <p style={styles.component}>in <strong>{componentName}</strong></p>
            )}
            <p style={styles.message}>
                {error?.message || 'An unexpected error occurred while rendering this section.'}
            </p>

            <div style={styles.actions}>
                <button style={styles.btnPrimary} onClick={onReset}>
                    Try Again
                </button>
                <button style={styles.btnSecondary} onClick={() => window.location.reload()}>
                    Reload Page
                </button>
            </div>

            {/* Dev-only stack trace */}
            {process.env.NODE_ENV === 'development' && error?.stack && (
                <details style={styles.details}>
                    <summary style={styles.summary}>Stack trace</summary>
                    <pre style={styles.stack}>{error.stack}</pre>
                </details>
            )}
        </div>
    </div>
);

// ─── Error Boundary class ───────────────────────────────────────────────────

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
        this.handleReset = this.handleReset.bind(this);
    }

    // Called during render when a child throws.
    // Return value is merged into state.
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    // Called after render with error details.
    // Good place to send to an error tracking service.
    componentDidCatch(error, info) {
        const { onError, componentName } = this.props;

        // Log to console in dev
        if (process.env.NODE_ENV === 'development') {
            console.error(
                `[ErrorBoundary] Caught in <${componentName || 'Unknown'}>:`,
                error,
                info.componentStack
            );
        }

        // Fire optional external error handler (e.g. Sentry, your Logger, etc.)
        if (typeof onError === 'function') {
            onError(error, info, componentName);
        }
    }

    handleReset() {
        this.setState({ hasError: false, error: null });
        if (typeof this.props.onReset === 'function') {
            this.props.onReset();
        }
    }

    render() {
        const { hasError, error } = this.state;
        const { children, fallback, componentName } = this.props;

        if (!hasError) return children;

        // Allow a fully custom fallback UI via the `fallback` prop
        if (fallback) {
            return typeof fallback === 'function'
                ? fallback({ error, onReset: this.handleReset })
                : fallback;
        }

        return (
            <DefaultFallback
                error={error}
                componentName={componentName}
                onReset={this.handleReset}
            />
        );
    }
}

export default ErrorBoundary;

const styles = {
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        padding: '24px',
        background: 'transparent',
    },
    card: {
        background: '#fff',
        border: '1px solid #fee2e2',
        borderRadius: '12px',
        padding: '36px 40px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(239,68,68,0.08)',
    },
    iconRow: {
        marginBottom: '12px',
    },
    icon: {
        fontSize: '2.5rem',
        color: '#ef4444',
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: 700,
        color: '#1e293b',
        margin: '0 0 4px',
    },
    component: {
        fontSize: '0.8rem',
        color: '#94a3b8',
        margin: '0 0 12px',
    },
    message: {
        fontSize: '0.9rem',
        color: '#64748b',
        margin: '0 0 24px',
        lineHeight: 1.6,
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginBottom: '20px',
    },
    btnPrimary: {
        padding: '9px 22px',
        background: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '0.875rem',
        cursor: 'pointer',
    },
    btnSecondary: {
        padding: '9px 22px',
        background: '#f1f5f9',
        color: '#475569',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '0.875rem',
        cursor: 'pointer',
    },
    details: {
        textAlign: 'left',
        marginTop: '12px',
    },
    summary: {
        fontSize: '0.8rem',
        color: '#94a3b8',
        cursor: 'pointer',
    },
    stack: {
        marginTop: '8px',
        padding: '12px',
        background: '#f8fafc',
        borderRadius: '6px',
        fontSize: '0.7rem',
        color: '#64748b',
        overflow: 'auto',
        maxHeight: '180px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
};